import React, { useState, useEffect } from 'react';
import {
  FiActivity as ActivityIcon,
  FiCheckCircle as CheckIcon,
  FiXCircle as XIcon,
  FiAlertTriangle as WarningIcon,
  FiPlay as PlayIcon,
  FiLink as LinkIcon,
  FiX as CloseIcon,
} from 'react-icons/fi';
import { eventsAPI, catalogAPI } from '../../../services/dataopsApi';
import { useProject } from '../../../contexts/ProjectContext';
import { useFilter } from '../../../contexts/FilterContext';

interface EventOverview {
  total_events: number;
  success_rate: number;
  failed_jobs: number;
  running_jobs: number;
}

interface DataEvent {
  id: string;
  event_type: string;
  status: string;
  description: string;
  job_id?: string | null;
  dataset_id?: string | null;
  created_at: string;
  tags?: string[];
  workload_name?: string;
}

type EventStatus = 'SUCCESS' | 'FAILED' | 'WARNING' | 'RUNNING';

function getEventIcon(eventType: string, status: string): React.ReactElement {
  if (status === 'SUCCESS') return <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />;
  if (status === 'FAILED') return <XIcon className="w-5 h-5 text-red-600 dark:text-red-400" />;
  if (status === 'WARNING') return <WarningIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
  if (status === 'RUNNING') return <PlayIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
  if (eventType === 'Lineage Update') return <LinkIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
  return <ActivityIcon className="w-5 h-5 text-gray-400" />;
}

function getStatusColor(status: string): string {
  switch (status as EventStatus) {
    case 'SUCCESS':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    case 'FAILED':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    case 'WARNING':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
    case 'RUNNING':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

const EventsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<EventOverview | null>(null);
  const [events, setEvents] = useState<DataEvent[]>([]);
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventDetail, setEventDetail] = useState<DataEvent | null>(null);
  const [eventDetailLoading, setEventDetailLoading] = useState(false);
  const [eventDetailError, setEventDetailError] = useState<string | null>(null);
  const [jobProjectMap, setJobProjectMap] = useState<Record<string, string | null>>({});
  const { selectedProjectId, projects } = useProject();
  const { filterType, selectedId: filterId } = useFilter();

  const getProjectName = (projectId: string | null | undefined): string | null => {
    if (!projectId) return null;
    return projects.find(p => p.id === projectId)?.name ?? null;
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter, selectedProjectId, filterType, filterId]);

  useEffect(() => {
    if (!viewModalOpen || !selectedEventId) return;
    setEventDetail(null);
    setEventDetailError(null);
    setEventDetailLoading(true);
    eventsAPI
      .getEvent(selectedEventId)
      .then((detail: DataEvent) => setEventDetail(detail))
      .catch((err: unknown) =>
        setEventDetailError(
          err instanceof Error ? err.message : 'Failed to load event details'
        )
      )
      .finally(() => setEventDetailLoading(false));
  }, [viewModalOpen, selectedEventId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewData, eventsData, jobsData] = await Promise.all([
        eventsAPI.getOverview(),
        eventsAPI.getEvents({
          event_type: typeFilter === 'All Types' ? undefined : typeFilter,
          status: statusFilter === 'All Status' ? undefined : statusFilter,
        }),
        catalogAPI.getJobs(),
      ]);
      setOverview(overviewData);
      setEvents(eventsData);
      const map: Record<string, string | null> = {};
      for (const job of jobsData) {
        map[job.id] = job.project_id ?? null;
      }
      setJobProjectMap(map);
    } catch (err: unknown) {
      console.error('Error loading events data:', err instanceof Error ? err.message : err);
    } finally {
      setLoading(false);
    }
  };

  const openViewEvent = (id: string) => {
    setSelectedEventId(id);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedEventId(null);
    setEventDetail(null);
    setEventDetailError(null);
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <ActivityIcon className="w-12 h-12 text-blue-600 dark:text-blue-400 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Pipeline Events</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">Monitor data pipeline executions and events in real-time</p>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <ActivityIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Events</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{overview?.total_events || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <CheckIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Success Rate</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{overview?.success_rate?.toFixed(1) || 0}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <XIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Failed Jobs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{overview?.failed_jobs || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <PlayIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Running Jobs</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{overview?.running_jobs || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="type" className="text-sm font-medium text-gray-700 dark:text-gray-300">Type:</label>
          <select id="type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option>All Types</option>
            <option>Job Start</option>
            <option>Job Complete</option>
            <option>Job Failed</option>
            <option>Data Quality</option>
            <option>Lineage Update</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="status" className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
          <select id="status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option>All Status</option>
            <option>SUCCESS</option>
            <option>FAILED</option>
            <option>WARNING</option>
            <option>RUNNING</option>
          </select>
        </div>
      </div>

      {/* Events List */}
      <div className="space-y-4">
        {events.map((event) => (
          <div
            key={event.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <div className="mt-1">
                  {getEventIcon(event.event_type, event.status)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {event.job_id ? `Job ${event.job_id}` : 'System Event'}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                      {event.status}
                    </span>
                    {event.job_id && getProjectName(jobProjectMap[event.job_id]) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                        {getProjectName(jobProjectMap[event.job_id])}
                      </span>
                    )}
                    {event.workload_name && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                        {event.workload_name}
                      </span>
                    )}
                    <span className="text-sm text-gray-500 dark:text-gray-400">{event.event_type}</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{event.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span>{new Date(event.created_at).toLocaleString()}</span>
                    {(event.tags?.length ?? 0) > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {event.tags!.map((tag) => (
                          <span key={`tag-${event.id}-${tag}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => openViewEvent(event.id)}
                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
              >
                View &gt;
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* View event detail modal */}
      {viewModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"
              onClick={closeViewModal}
              onKeyDown={(e) => e.key === 'Escape' && closeViewModal()}
              role="button"
              tabIndex={0}
              aria-label="Close modal"
            />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Event details</h3>
                  <button
                    type="button"
                    onClick={closeViewModal}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <CloseIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                {eventDetailLoading && (
                  <div className="py-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>
                )}
                {eventDetailError && (
                  <div className="py-4 text-red-600 dark:text-red-400 text-sm">{eventDetailError}</div>
                )}
                {!eventDetailLoading && !eventDetailError && eventDetail && (
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="font-medium text-gray-500 dark:text-gray-400">Type</dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-white">{eventDetail.event_type}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500 dark:text-gray-400">Status</dt>
                      <dd className="mt-0.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(eventDetail.status)}`}>
                          {eventDetail.status}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500 dark:text-gray-400">Description</dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-white">{eventDetail.description}</dd>
                    </div>
                    {eventDetail.job_id && (
                      <div>
                        <dt className="font-medium text-gray-500 dark:text-gray-400">Job ID</dt>
                        <dd className="mt-0.5 text-gray-900 dark:text-white font-mono text-xs">{eventDetail.job_id}</dd>
                      </div>
                    )}
                    {eventDetail.dataset_id && (
                      <div>
                        <dt className="font-medium text-gray-500 dark:text-gray-400">Dataset ID</dt>
                        <dd className="mt-0.5 text-gray-900 dark:text-white font-mono text-xs">{eventDetail.dataset_id}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="font-medium text-gray-500 dark:text-gray-400">Created</dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-white">
                        {eventDetail.created_at ? new Date(eventDetail.created_at).toLocaleString() : '-'}
                      </dd>
                    </div>
                    {(eventDetail.tags?.length ?? 0) > 0 && (
                      <div>
                        <dt className="font-medium text-gray-500 dark:text-gray-400">Tags</dt>
                        <dd className="mt-1 flex flex-wrap gap-1">
                          {eventDetail.tags!.map((tag) => (
                            <span key={`detail-tag-${tag}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                              {tag}
                            </span>
                          ))}
                        </dd>
                      </div>
                    )}
                  </dl>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
